import { catalog } from './catalog.js';
import { db } from './db.js';
import { FilesClient } from './files.js';

/* Datos de un vídeo propio corregidos a mano (título, año, sinopsis, género, valoración y clasificación por edades). Los vídeos de
   una carpeta, un NAS o una nube no tienen ficha: el título es el nombre del fichero. Lo que se escribe se guarda en el dispositivo
   (junto al catálogo, por título) y se vuelve a poner cada vez que el catálogo se recarga, sin tocar el original: se puede restaurar. */

export const FIELDS = ['name', 'year', 'plot', 'genre', 'rating', 'age', 'tmdb']; // `tmdb`: id al que se enlazó a mano (vacío = sin enlace)
export const AGES = ['TP', '+7', '+12', '+16', '+18'];

/** Los datos editables de un título tal como están ahora. */
export function pickFields(item) {
    const out = {};
    FIELDS.forEach((f) => { out[f] = item && item[f] != null ? item[f] : ''; });
    return out;
}

/** Lo que escribe la persona → datos limpios, o { error: 'year' | 'rating' | 'name' } si algo no vale. Los vacíos se guardan vacíos. */
export function cleanValues(input) {
    const v = {};
    v.name = String(input.name == null ? '' : input.name).replace(/\s+/g, ' ').trim().slice(0, 200);
    if (!v.name) return { error: 'name' };
    const year = String(input.year == null ? '' : input.year).trim();
    if (year && !(/^\d{4}$/.test(year) && Number(year) >= 1880 && Number(year) <= 2100)) return { error: 'year' };
    v.year = year;
    const rating = String(input.rating == null ? '' : input.rating).trim().replace(',', '.');
    if (rating) {
        const n = Number(rating);
        if (!isFinite(n) || n < 0 || n > 10) return { error: 'rating' };
        v.rating = Math.round(n * 10) / 10;
    } else {
        v.rating = 0;
    }
    v.plot = String(input.plot == null ? '' : input.plot).trim().slice(0, 4000);
    v.genre = String(input.genre == null ? '' : input.genre).split(/[,;/]+/).map((g) => g.trim()).filter(Boolean).slice(0, 8).join(', ');
    const age = String(input.age == null ? '' : input.age).trim();
    v.age = AGES.indexOf(age) !== -1 ? age : '';
    const tmdb = String(input.tmdb == null ? '' : input.tmdb).trim();
    v.tmdb = /^\d{1,9}$/.test(tmdb) ? tmdb : '';
    return { values: v };
}

/** Pone los datos editados sobre el título (en el sitio: el catálogo y las fichas comparten el objeto). */
export function applyFields(item, values) {
    if (!item || !values) return item;
    FIELDS.forEach((f) => { if (values[f] !== undefined) item[f] = values[f]; });
    item.edited = true;
    return item;
}

let map = null; // «vod:id» → { v: datos editados, orig: datos originales, at }
let mapFor = '';
let loading = null;

async function load() {
    const key = 'edits:' + catalog.id;
    if (map && mapFor === key) return map;
    if (loading && mapFor === key) return loading;
    mapFor = key;
    loading = db.get('meta', key).catch(() => null).then((m) => { map = m && typeof m === 'object' ? m : {}; loading = null; return map; });
    return loading;
}

const persist = () => db.put('meta', mapFor, map).catch(() => {});

function applyAll() {
    if (!map) return;
    Object.keys(map).forEach((key) => {
        const [kind, ...rest] = key.split(':');
        const item = catalog.get(kind, rest.join(':'));
        if (item) applyFields(item, map[key].v);
    });
}

export const itemEdits = {
    /** ¿Se puede editar a mano? Solo lo que sale de una carpeta, un NAS, este aparato o una nube (el resto trae su ficha). */
    canEdit(item) {
        return !!item && catalog._client(item) instanceof FilesClient;
    },

    /** Carga lo guardado y lo aplica a lo que haya en el catálogo. */
    async preload() {
        await load();
        applyAll();
    },

    /** Lo editado de un título (o null). Hay que haber llamado antes a preload (se hace al cargar el catálogo). */
    get(kind, id) {
        return map && mapFor === 'edits:' + catalog.id ? map[kind + ':' + id] || null : null;
    },

    async save(kind, id, values) {
        const item = catalog.get(kind, id);
        if (!item) return false;
        await load();
        const key = kind + ':' + id;
        const prev = map[key];
        const prevTmdb = String(item.tmdb || '');
        map[key] = { v: values, orig: prev ? prev.orig : pickFields(item), at: Date.now() };
        applyFields(item, values);
        const cached = catalog.peekInfo(kind, id); // la ficha ya cargada es otro objeto: también se actualiza
        if (cached) applyFields(cached, values);
        // Enlazar o desenlazar con TMDB cambia lo que hay que pedir para la ficha (reparto, tráiler…): se rehace al abrirla.
        if (prevTmdb !== String(values.tmdb || '')) await catalog.dropDetails(kind, id);
        await persist();
        return true;
    },

    /** Vuelve a los datos de siempre. */
    async restore(kind, id) {
        await load();
        const key = kind + ':' + id;
        const entry = map[key];
        if (!entry) return false;
        const item = catalog.get(kind, id);
        const cached = catalog.peekInfo(kind, id);
        const hadTmdb = String(item && item.tmdb || '');
        [item, cached].forEach((o) => {
            if (!o) return;
            FIELDS.forEach((f) => { o[f] = entry.orig[f]; });
            o.edited = false;
        });
        delete map[key];
        if (hadTmdb !== String(entry.orig.tmdb || '')) await catalog.dropDetails(kind, id);
        await persist();
        return true;
    }
};

// La ficha completa de un título (con TMDB, episodios…) también lleva lo editado, venga de la memoria o de lo guardado.
catalog.editApply = (kind, id, obj) => {
    const e = itemEdits.get(kind, id);
    if (e) applyFields(obj, e.v);
};

// Al cargar o recargar el catálogo se vuelven a poner los datos editados.
catalog.on('change', () => { if (catalog.loaded) itemEdits.preload().catch(() => {}); });
